import { apiClient } from '../utils/api-client';
import type {
  Competition,
  CompetitionStatus,
  CompetitionStats,
  CompetitionParticipant,
  CompetitionCategory,
  CompetitionTeam,
  CompetitionResponse,
  CompetitionStatsResponse,
  CompetitionParticipantResponse,
  CompetitionParticipantsListResponse,
  CompetitionCategoriesListResponse,
  CompetitionTeamsListResponse
} from '../types/competition';
import type { EntryDetails, EntryOptions, EntryPreview, StageParticipation } from '../types/trophy';
import type { HeldWard, NewWard } from './guardianships';
import { throwForResponse } from './api-error';

export interface AddParticipantData {
  email: string;
  name?: string;
  surname?: string;
  competition_category_id?: string;
  competition_team_id?: string;
}

/**
 * One child as the guided sign-up sends them: who they are, the gender of the
 * class they compete in (required: it is saved on the account opened for
 * them), and the turn picked when their class is split over two or more.
 */
export interface SignUpAthlete extends NewWard {
  gender: 'male' | 'female';
  competition_round_id?: string;
}

/**
 * What a parent with no account sends to enter their children: the children,
 * and themselves — either as a form or as a Google credential, never both.
 */
export interface GuardianSignUp {
  athletes: SignUpAthlete[];
  guardian?: {
    name: string;
    surname: string;
    username: string;
    email: string;
    password: string;
    password_confirmation: string;
    language: string;
  };
  credential?: string;
  locale: string;
}

/** The session the guided sign-up answers with, and who it entered. */
export interface GuardianSignUpResult {
  token: string;
  refresh_token: string;
  resource_owner: unknown;
  athletes: HeldWard[];
}

/** The request body of the entry endpoints: who, and what the dialog typed. */
function entryBody(forUserId: string | null | undefined, details: EntryDetails = {}) {
  return {
    ...(forUserId ? { for_user_id: forUserId } : {}),
    ...(details.gender ? { gender: details.gender } : {}),
    ...(details.birth_date ? { birth_date: details.birth_date } : {}),
    ...(details.competition_round_id ? { competition_round_id: details.competition_round_id } : {}),
  };
}

class CompetitionsService {
  /**
   * Enters the signed-in person in this competition. The server fills the
   * entry from their profile and whatever the circuit already knows about them;
   * `details` is what the entry dialog asked for — the gender and date of birth
   * nobody knew yet, and the turn picked — each of which only fills a blank.
   * Answers with where they now stand, ready to redraw the row.
   *
   * `forUserId` enters a child whose managed account the viewer holds, rather
   * than the viewer. The server only honours an account they are guardian of.
   *
   * A refusal carries its codes (`gender_required`, `round_required`, …) so the
   * dialog can put each one under its field — see `errorCodesOf`.
   */
  async enter(slug: string, forUserId?: string | null, details: EntryDetails = {}): Promise<StageParticipation> {
    const response = await apiClient.post(`/api/competitions/${slug}/entry`, entryBody(forUserId, details));

    if (!response.ok) {
      await throwForResponse(response, 'Failed to enter the competition');
    }

    const data: { data: { attributes: StageParticipation } } = await response.json();
    return data.data.attributes;
  }

  /**
   * What the entry dialog shows before anybody confirms: the values it still
   * has to ask, and the category and rounds they lead to. A POST, so that a
   * typed date of birth never sits in a URL.
   */
  async previewEntry(slug: string, forUserId?: string | null, details: EntryDetails = {}): Promise<EntryPreview> {
    const response = await apiClient.post(`/api/competitions/${slug}/entry/preview`, entryBody(forUserId, details));

    if (!response.ok) {
      await throwForResponse(response, 'Could not prepare the entry');
    }

    const data: { data: EntryPreview } = await response.json();
    return data.data;
  }

  /**
   * The category and rounds a gender and a date of birth lead to here, with
   * nobody signed in to read — the guided sign-up and the team manager's
   * dialog. Public on the server.
   *
   * `category_id` keeps a class as it is rather than working one out: a squad's
   * row the desk sorted by hand, which a corrected date does not move.
   */
  async entryOptions(
    slug: string,
    details: { gender?: string | null; birth_date?: string | null; category_id?: string | null }
  ): Promise<EntryOptions> {
    const response = await apiClient.post(`/api/competitions/${slug}/entry_options`, {
      gender: details.gender ?? null,
      birth_date: details.birth_date ?? null,
      ...(details.category_id ? { category_id: details.category_id } : {}),
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not read the categories');
    }

    const data: { data: EntryOptions } = await response.json();
    return data.data;
  }

  /**
   * Books a turn on an existing entry, or moves it to another — the viewer's,
   * or a child's (`forUserId`). While entries are open and the turn booked has
   * not started.
   */
  async bookTurn(slug: string, roundId: string, forUserId?: string | null): Promise<StageParticipation> {
    const response = await apiClient.patch(`/api/competitions/${slug}/entry`, {
      ...entryBody(forUserId),
      competition_round_id: roundId,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not book the turn');
    }

    const data: { data: { attributes: StageParticipation } } = await response.json();
    return data.data.attributes;
  }

  /**
   * Takes the name back off the start list: the viewer's own, or a child's
   * (`forUserId`). The server refuses once the event is under way or a card
   * has been written — from there it is the organiser's to undo, which is
   * what the dialog behind this says about money too.
   */
  async withdraw(slug: string, forUserId?: string | null): Promise<void> {
    const query = forUserId ? `?for_user_id=${encodeURIComponent(forUserId)}` : '';
    const response = await apiClient.delete(`/api/competitions/${slug}/entry${query}`);

    if (!response.ok) {
      await throwForResponse(response, 'Could not withdraw the entry');
    }
  }

  /**
   * A parent with no account, entering their children here: their account, the
   * children's accounts and the entries in one request, so there is no state
   * where some of it happened (see Api::Competitions::SignUpsController).
   *
   * Answers with a session, which the caller hands to the auth context.
   *
   * `declaration` is the art. 8 consent, added here as `createWard` does: the
   * server writes nothing without it, and the page has already made it a
   * deliberate tick before it can call this.
   */
  async signUpAndEnter(slug: string, payload: GuardianSignUp): Promise<GuardianSignUpResult> {
    const response = await apiClient.post(`/api/competitions/${slug}/sign_up`, {
      ...payload,
      declaration: true,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not complete the sign-up');
    }

    return (await response.json()) as GuardianSignUpResult;
  }

  async getCompetitionBySlug(slug: string): Promise<Competition> {
    const response = await apiClient.get(`/api/competitions/${slug}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Competition not found');
      }
      throw new Error('Failed to fetch competition profile');
    }

    const data: CompetitionResponse = await response.json();
    return data.data.attributes;
  }

  async getCompetitionStats(competitionId: string): Promise<CompetitionStats> {
    const response = await apiClient.get(`/api/competitions/${competitionId}/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch competition stats');
    }

    const data: CompetitionStatsResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * `confirmed` narrows the list to the entries the desk has confirmed — the
   * people actually climbing, which is all a judging screen should show.
   */
  /**
   * The entry list. `confirmed` narrows it to the people the desk says are
   * actually climbing; `team: 'mine'` to the athletes entered for a squad the
   * viewer is a team manager of — which is the only list a team manager's
   * scoring page has any business showing, and is narrowed by the server rather
   * than here.
   */
  async getParticipants(
    competitionId: string,
    options: { confirmed?: boolean; team?: 'mine'; roundId?: string } = {}
  ): Promise<CompetitionParticipant[]> {
    const params = new URLSearchParams();
    if (options.confirmed) params.set('confirmed', 'true');
    if (options.team) params.set('team', options.team);
    // The people climbing one round: its classes, and on a turn the ones who
    // booked it or have not booked any.
    if (options.roundId) params.set('round_id', options.roundId);
    const query = params.size > 0 ? `?${params}` : '';
    const response = await apiClient.get(`/api/competitions/${competitionId}/participants${query}`);

    if (!response.ok) {
      throw new Error('Failed to fetch participants');
    }

    const data: CompetitionParticipantsListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  async getCategories(competitionId: string): Promise<CompetitionCategory[]> {
    const response = await apiClient.get(`/api/competitions/${competitionId}/categories`);

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }

    const data: CompetitionCategoriesListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  async getTeams(competitionId: string): Promise<CompetitionTeam[]> {
    const response = await apiClient.get(`/api/competitions/${competitionId}/teams`);

    if (!response.ok) {
      throw new Error('Failed to fetch teams');
    }

    const data: CompetitionTeamsListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  async addParticipant(competitionId: string, data: AddParticipantData): Promise<CompetitionParticipant> {
    const response = await apiClient.post(`/api/competitions/${competitionId}/participants`, {
      data: {
        attributes: data
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to add participant');
    }

    const result: CompetitionParticipantResponse = await response.json();
    return result.data.attributes;
  }

  async updateParticipant(competitionId: string, participantId: string, data: Partial<AddParticipantData>): Promise<CompetitionParticipant> {
    const response = await apiClient.patch(`/api/competitions/${competitionId}/participants/${participantId}`, {
      data: {
        attributes: data
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to update participant');
    }

    const result: CompetitionParticipantResponse = await response.json();
    return result.data.attributes;
  }

  async removeParticipant(competitionId: string, participantId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/competitions/${competitionId}/participants/${participantId}`
    );

    if (!response.ok) {
      throw new Error('Failed to remove participant');
    }
  }

  async updateCompetition(competitionSlug: string, data: Partial<Competition>): Promise<Competition> {
    const response = await apiClient.patch(`/api/competitions/${competitionSlug}`, {
      data: {
        attributes: data
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.errors?.[0]?.detail || 'Failed to update competition';
      throw new Error(errorMessage);
    }

    const responseData: CompetitionResponse = await response.json();
    return responseData.data.attributes;
  }

  /**
   * Move the competition through its life cycle. Its own endpoint, not a field
   * on `updateCompetition`: the status is what decides who can see the page
   * and who can sign up, and an ordinary edit has no business moving it.
   *
   * The server refuses a move the life cycle does not allow, and says why
   * (`invalid_transition`, `competition_has_entries`).
   */
  async updateStatus(competitionSlug: string, status: CompetitionStatus): Promise<Competition> {
    const response = await apiClient.patch(`/api/competitions/${competitionSlug}/status`, {
      data: { attributes: { status } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to change the status');
    }

    const responseData: CompetitionResponse = await response.json();
    return responseData.data.attributes;
  }
}

export const competitionsService = new CompetitionsService();
